// Contenu propre à chaque département couvert.
//
// Pourquoi ce fichier : les 6 pages départementales rendaient toutes le même
// composant avec le seul nom du département qui changeait. Google traite ce
// type de page comme du contenu dupliqué et n'en indexe qu'une. Chaque
// département a donc ici ses aides locales réelles, son contexte de bâti et
// ses questions propres.
//
// Règle de conformité (voir en-tête de donneesStructurees.js) : on ne publie
// que des montants sourcés, et de préférence exprimés en pourcentage ou en
// plafond annoncé par l'organisme lui-même. `source` doit toujours pointer
// vers la page officielle du dispositif. Aucun montant inventé, aucune
// moyenne « maison ».

export const DEPARTEMENTS = {
  paris: {
    code: "75",
    nom: "Paris",
    // « à Paris », « en Seine-et-Marne », « dans l'Essonne » : la préposition
    // ne se déduit pas du nom, elle est portée par la donnée.
    ou: "à Paris",
    de: "de Paris",
    intro:
      "À Paris, l'adaptation du logement se heurte moins au budget qu'au bâti : plus de neuf logements sur dix sont des appartements, souvent dans des immeubles anciens, avec des salles de bain de 2 à 4 m² et des parties communes qui relèvent de la copropriété. Le montage d'un dossier MaPrimeAdapt' y demande donc deux choses en plus d'ailleurs : un plan de calepinage qui fait tenir une douche de plain-pied dans l'existant, et la bonne articulation avec le syndic quand les travaux touchent aux parties communes.",
    contexte: {
      titre: "Ce qui change quand on habite Paris",
      points: [
        {
          titre: "Salle de bain exiguë",
          texte:
            "Remplacer une baignoire par une douche de plain-pied dans 3 m² se joue au centimètre : receveur extra-plat, paroi escamotable, déplacement de l'évacuation. C'est faisable dans la très grande majorité des cas, mais cela se dessine avant de chiffrer.",
        },
        {
          titre: "Travaux en parties communes",
          texte:
            "Un monte-escalier ou une rampe dans la cage d'escalier ne relève pas de vous seul : il faut l'accord de l'assemblée générale des copropriétaires. Il vaut mieux inscrire la question à l'ordre du jour de l'AG à venir avant de déposer le dossier, sous peine de bloquer le calendrier de plusieurs mois.",
        },
        {
          titre: "Immeuble sans ascenseur",
          texte:
            "Beaucoup d'immeubles haussmanniens n'en ont pas. Quand l'ascenseur est impossible, on travaille sur l'intérieur du logement et sur la sécurisation des montées : main courante continue, éclairage automatique des paliers, nez de marches contrastés.",
        },
      ],
    },
    aidesLocales: {
      // L'ANIL ne recense aucune subvention départementale parisienne dédiée à
      // l'adaptation : on le dit, plutôt que d'inventer une ligne d'aide.
      preambule:
        "Paris n'a pas de subvention départementale dédiée à l'adaptation du logement comparable à celle des Hauts-de-Seine ou de la Seine-Saint-Denis. Le bon interlocuteur pour les aides facultatives est le Centre d'action sociale de la Ville de Paris (CASVP), qui dispose d'une section dans chaque arrondissement.",
      dispositifs: [
        {
          nom: "Centre d'action sociale de la Ville de Paris (CASVP)",
          texte:
            "Le CASVP instruit les aides sociales facultatives de la Ville, au cas par cas et selon vos ressources. On vous oriente vers la section de votre arrondissement au moment du montage du dossier, pour savoir ce qui peut compléter MaPrimeAdapt' dans votre situation.",
          source: "https://www.paris.fr/pages/aides-sociales-pour-les-seniors-2968",
        },
        {
          nom: "Caisse de retraite et APA",
          texte:
            "Pour les Parisiens qui ne sont pas éligibles à MaPrimeAdapt', la caisse de retraite principale (CNAV pour le régime général) et le volet aides techniques de l'APA restent les deux leviers à examiner.",
          source:
            "https://www.pour-les-personnes-agees.gouv.fr/preserver-son-autonomie/amenager-son-logement-et-s-equiper/les-aides-financieres-pour-adapter-son-logement",
        },
      ],
    },
        // Requêtes locales : « adaptation logement personne âgée <ville> » et
    // « aide salle de bain senior <département> ». Peu concurrentielles, et
    // le contenu reprend le contexte de bâti décrit plus haut — pas du
    // remplissage à mots-clés.
    local: {
      villes: "Paris 15e, 16e, 17e, 18e, 19e et 20e",
      adaptation:
        "L'adaptation du logement d'une personne âgée à Paris se heurte presque toujours au même obstacle : la surface. Une salle de bain parisienne fait souvent moins de 4 m², et l'escalier de l'immeuble n'appartient pas au logement. Nous chiffrons d'abord ce qui relève des parties privatives — la douche, les seuils, l'éclairage — avant d'aborder ce qui suppose l'accord de la copropriété.",
      salleDeBain:
        "L'aide pour une salle de bain senior à Paris couvre le remplacement de la baignoire par une douche de plain-pied, le sol antidérapant, les barres d'appui et l'éclairage sécurisé. Dans les 20 arrondissements, ces travaux se font sans toucher au gros œuvre dans la grande majorité des cas.",
    },
communes: [
      "les 20 arrondissements",
      "Paris 15e",
      "Paris 16e",
      "Paris 17e",
      "Paris 18e",
      "Paris 19e",
      "Paris 20e",
      "Paris 12e",
      "Paris 13e",
      "Paris 14e",
    ],
    faq: [
      {
        question: "Puis-je installer un monte-escalier dans une copropriété parisienne ?",
        answer:
          "Oui, et le régime est plus favorable qu'on ne le croit. Depuis l'article 25-2 de la loi du 10 juillet 1965, un copropriétaire peut faire réaliser à ses frais des travaux d'accessibilité qui touchent aux parties communes sans avoir à obtenir un vote d'autorisation. Il notifie au syndic une demande d'inscription à l'ordre du jour de la prochaine assemblée générale, à titre d'information, avec un descriptif détaillé des travaux. L'assemblée ne peut s'y opposer que par une décision motivée, fondée sur l'atteinte à la structure de l'immeuble ou à ses équipements essentiels, ou sur la non-conformité à la destination de l'immeuble. À l'intérieur du logement, sur un escalier privatif de duplex, aucune formalité n'est nécessaire.",
      },
      {
        question: "Ma salle de bain parisienne fait 3 m², une douche sénior est-elle possible ?",
        answer:
          "Dans la très grande majorité des cas, oui. On utilise un receveur extra-plat posé au niveau du sol, une paroi fixe ou escamotable plutôt qu'une porte, et on repositionne le lavabo pour dégager l'aire de rotation. La contrainte réelle est l'évacuation : si la chute est éloignée, il faut soit une légère surélévation, soit une pompe de relevage. Cela se vérifie pendant la visite d'évaluation à domicile.",
      },
      {
        question: "Suis-je éligible à MaPrimeAdapt' si je suis locataire à Paris ?",
        answer:
          "MaPrimeAdapt' s'adresse aux propriétaires occupants, et aux propriétaires bailleurs sous conditions. En tant que locataire du parc privé, la demande passe par votre propriétaire. Si vous êtes locataire d'un bailleur social, c'est le bailleur qu'il faut saisir : les travaux d'adaptation relèvent de sa programmation.",
      },
    ],
  },

  "seine-et-marne": {
    code: "77",
    nom: "Seine-et-Marne",
    ou: "en Seine-et-Marne",
    de: "de Seine-et-Marne",
    intro:
      "La Seine-et-Marne est le plus vaste département d'Île-de-France, et c'est celui où la maison individuelle domine le plus largement. Concrètement, les dossiers d'adaptation qu'on y monte ressemblent peu à ceux de Paris : ce sont des pavillons à étage, où la chambre et la salle de bain sont au premier, et où la question posée est presque toujours la même — monter, ou redescendre le couchage au rez-de-chaussée.",
    contexte: {
      titre: "Ce qui change quand on habite la Seine-et-Marne",
      points: [
        {
          titre: "Le pavillon à étage",
          texte:
            "Deux stratégies s'opposent, et le choix a de grosses conséquences budgétaires : installer un monte-escalier, ou transférer chambre et salle d'eau au rez-de-chaussée. La seconde option coûte plus cher au départ mais règle la question définitivement. On chiffre les deux avant de trancher.",
        },
        {
          titre: "Des distances réelles",
          texte:
            "Entre Provins et Chelles, il y a plus de 60 km. Les artisans qui interviennent près de Melun ne sont pas ceux qui interviennent près de Meaux, et cela se voit dans les devis. Le maillage d'entreprises locales évite les frais de déplacement qui gonflent la facture.",
        },
        {
          titre: "Les extérieurs comptent",
          texte:
            "Les accès au pavillon — allée en gravier, marches du perron, seuil du garage — sont la première cause de chute déclarée. Ces travaux sont éligibles au même titre que la salle de bain, et on les oublie souvent dans les dossiers.",
        },
      ],
    },
    aidesLocales: {
      preambule:
        "Le Département soutient l'adaptation du logement par ses aides techniques et son volet autonomie, plutôt que par une subvention travaux dédiée. Le prêt de la caisse départementale, longtemps le complément le plus utile, n'est en revanche plus distribué.",
      dispositifs: [
        {
          // Vérifié le 17 août 2026 : le site de la CADAL affiche « ACTIVITÉ DE
          // PRÊTEUR ARRÊTÉE ». La page départementale « aides aux seniors »
          // annonce pourtant toujours les prêts à 2 % — elle n'a pas été mise à
          // jour. Nous signalons l'écart plutôt que de relayer une aide qui
          // n'existe plus : un visiteur qui compte dessus pour boucler son plan
          // de financement se retrouverait bloqué au moment de payer.
          nom: "CADAL 77 — prêt à 2 %, activité arrêtée",
          texte:
            "La Caisse départementale d'aide au logement proposait des prêts à taux réduit pour les travaux d'amélioration, remboursables sur plusieurs années. Son site indique désormais que l'activité de prêteur est arrêtée, alors que la page du Département la mentionne encore. Ne comptez pas sur ce prêt pour financer votre reste à charge sans avoir appelé la CADAL au 01 60 65 94 88 pour confirmation.",
          source: "https://www.seine-et-marne.fr/fr/aides-aux-seniors",
        },
        {
          nom: "Aides techniques du Département",
          texte:
            "Le Département finance des équipements préconisés par un ergothérapeute ou un médecin — barres d'appui, déambulateur, matériel de maintien à domicile. Ils ne relèvent pas des travaux MaPrimeAdapt' mais complètent utilement un projet d'adaptation, et s'obtiennent auprès du service autonomie.",
          source: "https://www.seine-et-marne.fr/fr/aides-aux-seniors",
        },
        {
          nom: "Aides du Département aux seniors",
          texte:
            "Le Département soutient par ailleurs les personnes âgées propriétaires dans leurs travaux d'adaptation, sous conditions de ressources, avec des montants qui varient selon la nature des travaux.",
          source: "https://www.seine-et-marne.fr/fr/aides-aux-seniors",
        },
      ],
    },
        // Requêtes locales : « adaptation logement personne âgée <ville> » et
    // « aide salle de bain senior <département> ». Peu concurrentielles, et
    // le contenu reprend le contexte de bâti décrit plus haut — pas du
    // remplissage à mots-clés.
    local: {
      villes: "Meaux, Melun, Chelles, Torcy, Fontainebleau et Provins",
      adaptation:
        "En Seine-et-Marne, l'adaptation du logement d'une personne âgée concerne surtout des pavillons à étage. À Meaux comme à Melun, la question revient toujours : installer un monte-escalier, ou descendre chambre et salle d'eau au rez-de-chaussée. Nous chiffrons les deux avant de trancher, parce que le moins cher au départ n'est pas toujours le bon choix à dix ans.",
      salleDeBain:
        "L'aide pour une salle de bain senior en Seine-et-Marne finance jusqu'à 70 % d'une douche de plain-pied, du sol antidérapant et des barres d'appui. Dans un pavillon, on profite souvent du chantier pour créer une salle d'eau au rez-de-chaussée plutôt que de refaire celle de l'étage.",
    },
communes: [
      "Meaux",
      "Melun",
      "Chelles",
      "Torcy",
      "Fontainebleau",
      "Provins",
      "Coulommiers",
      "Nemours",
      "Lagny-sur-Marne",
      "Savigny-le-Temple",
    ],
    faq: [
      {
        question: "Monte-escalier ou chambre au rez-de-chaussée : que choisir dans un pavillon ?",
        answer:
          "Le monte-escalier coûte moins cher à l'installation et se pose en une journée, mais il ne résout que la montée : la salle de bain reste à l'étage. Créer une chambre et une salle d'eau au rez-de-chaussée coûte davantage, demande souvent de toucher aux réseaux, mais rend le logement viable même si l'autonomie se dégrade. Les deux sont éligibles à MaPrimeAdapt'. Nous chiffrons systématiquement les deux scénarios pendant la visite.",
      },
      {
        question: "Peut-on encore emprunter à la CADAL 77 pour le reste à charge ?",
        answer:
          "Ce n'est plus possible en l'état : la Caisse départementale d'aide au logement indique sur son site avoir arrêté son activité de prêteur, constaté le 17 août 2026. La page du Département consacrée aux aides aux seniors mentionne pourtant toujours ses prêts à 2 %, ce qui entretient la confusion. Appelez la CADAL au 01 60 65 94 88 avant de bâtir votre plan de financement dessus. Pour étaler un reste à charge, regardez plutôt du côté de votre caisse de retraite et du prêt à l'amélioration de l'habitat de la CAF.",
      },
      {
        question: "Intervenez-vous partout en Seine-et-Marne ?",
        answer:
          "Oui, sur l'ensemble du département, de Chelles à Provins et de Meaux à Nemours. La visite d'évaluation à domicile est gratuite et sans engagement, y compris dans les communes les plus éloignées.",
      },
    ],
  },

  essonne: {
    code: "91",
    nom: "Essonne",
    ou: "dans l'Essonne",
    de: "de l'Essonne",
    intro:
      "L'Essonne combine des zones pavillonnaires très étendues au nord du département et des communes plus rurales au sud. Le Département verse une aide individuelle à l'adaptation du logement, et l'agglomération Paris-Saclay ajoute son propre complément sur son territoire : c'est l'un des rares endroits d'Île-de-France où deux échelons locaux peuvent venir s'ajouter à MaPrimeAdapt'.",
    contexte: {
      titre: "Ce qui change quand on habite l'Essonne",
      points: [
        {
          titre: "Deux échelons d'aide locale",
          texte:
            "Selon votre commune, vous relevez ou non du territoire de l'agglomération Paris-Saclay, qui verse une subvention complémentaire à celle de l'ANAH pour l'adaptation. Le premier réflexe est donc de vérifier votre intercommunalité : le montant final n'est pas le même d'une commune à l'autre.",
        },
        {
          titre: "Un habitat très majoritairement pavillonnaire",
          texte:
            "Comme en Seine-et-Marne, la question de l'étage domine. S'y ajoute fréquemment le sous-sol semi-enterré avec le garage, dont l'escalier est souvent le plus dangereux de la maison.",
        },
        {
          titre: "Le sud du département",
          texte:
            "Autour d'Étampes et de Dourdan, les logements sont plus anciens et souvent moins bien isolés. Il est fréquent que la visite d'adaptation révèle aussi un enjeu énergétique : les deux dossiers ne se déposent pas au même endroit, mais nous vous signalons le point.",
        },
      ],
    },
    aidesLocales: {
      preambule:
        // Le cumul des deux dispositifs entre eux n'est documenté nulle part :
        // la formulation précédente l'affirmait, on renvoie désormais vers
        // l'ADIL 91, qui est l'organisme neutre compétent pour trancher.
        "Deux dispositifs locaux sont recensés par l'ANIL en Essonne, en plus de MaPrimeAdapt'. Leurs règles de cumul ne sont pas publiées : l'ADIL 91 est l'interlocuteur qui peut les préciser au regard de votre situation.",
      dispositifs: [
        {
          nom: "Aide départementale individuelle à l'adaptation du logement",
          texte:
            "Le Département de l'Essonne verse une aide individuelle aux propriétaires occupants pour les travaux d'adaptation liés à l'âge ou au handicap. Les conditions et le montant s'apprécient au dossier : l'ADIL 91 est l'interlocuteur neutre pour les faire préciser.",
          source: "https://www.anil.org/aides-locales-travaux/essonne/",
        },
        {
          nom: "Complément de l'agglomération Paris-Saclay",
          texte:
            "Sur le territoire de la communauté d'agglomération Paris-Saclay, une subvention communautaire complète celle de l'ANAH pour l'adaptation du logement des personnes âgées ou handicapées. Elle est ouverte aux propriétaires occupants comme aux bailleurs et aux locataires.",
          source: "https://www.anil.org/aides-locales-travaux/essonne/",
        },
      ],
    },
        // Requêtes locales : « adaptation logement personne âgée <ville> » et
    // « aide salle de bain senior <département> ». Peu concurrentielles, et
    // le contenu reprend le contexte de bâti décrit plus haut — pas du
    // remplissage à mots-clés.
    local: {
      villes: "Évry-Courcouronnes, Massy, Palaiseau, Corbeil-Essonnes et Savigny-sur-Orge",
      adaptation:
        "L'Essonne mêle grands ensembles et pavillonnaire, et l'adaptation du logement d'une personne âgée n'y prend pas la même forme selon le cas. À Évry-Courcouronnes ou Massy, il s'agit le plus souvent d'un appartement où tout se joue dans la salle de bain ; à Savigny-sur-Orge ou Sainte-Geneviève-des-Bois, d'une maison où l'escalier et le seuil d'entrée commandent le reste.",
      salleDeBain:
        "L'aide pour une salle de bain senior dans l'Essonne se cumule avec l'aide départementale à l'adaptation du logement. Le dossier MaPrimeAdapt' est déposé en premier, l'aide locale vient ensuite réduire le reste à charge.",
    },
communes: [
      "Évry-Courcouronnes",
      "Massy",
      "Palaiseau",
      "Corbeil-Essonnes",
      "Savigny-sur-Orge",
      "Sainte-Geneviève-des-Bois",
      "Athis-Mons",
      "Yerres",
      "Draveil",
      "Étampes",
    ],
    faq: [
      {
        question: "Comment savoir si je relève de l'aide Paris-Saclay ?",
        answer:
          "Le critère est votre commune de résidence : la communauté d'agglomération Paris-Saclay regroupe 27 communes du nord-ouest de l'Essonne, dont Palaiseau, Massy, Orsay et Les Ulis. Si vous en faites partie, la subvention communautaire vient s'ajouter à MaPrimeAdapt'. Nous vérifions ce point dès le premier échange, car il change le reste à charge.",
      },
      {
        question: "Les aides du Département et de l'agglomération se cumulent-elles entre elles ?",
        answer:
          "Non. L'aide départementale individuelle et le complément Paris-Saclay ne sont pas cumulables l'un avec l'autre. En revanche chacun se cumule avec MaPrimeAdapt'. On compare donc les deux pour retenir celui qui vous est le plus favorable.",
      },
      {
        question: "L'escalier de mon sous-sol est-il éligible ?",
        answer:
          "Oui. Les circulations intérieures font partie des travaux finançables : main courante des deux côtés, éclairage automatique, revêtement antidérapant, nez de marches contrastés, voire monte-escalier si la configuration le permet. C'est souvent l'escalier le plus accidentogène du pavillon, et il est régulièrement oublié dans les demandes.",
      },
    ],
  },

  "hauts-de-seine": {
    code: "92",
    nom: "Hauts-de-Seine",
    ou: "dans les Hauts-de-Seine",
    de: "des Hauts-de-Seine",
    intro:
      "Les Hauts-de-Seine sont le département francilien le mieux doté en aide locale à l'adaptation : le Département verse sa propre subvention, en plus de MaPrimeAdapt', avec une visite préalable d'ergothérapeute et une majoration pour les personnes les plus fragiles. C'est aussi notre département d'implantation — notre bureau est à Neuilly-sur-Seine.",
    contexte: {
      titre: "Ce qui change quand on habite les Hauts-de-Seine",
      points: [
        {
          titre: "Une aide départementale qui s'ajoute",
          texte:
            "C'est la différence majeure avec Paris. Le Département finance une part des travaux d'adaptation en complément de l'ANAH, ce qui fait mécaniquement baisser le reste à charge sur un même projet.",
        },
        {
          titre: "Une visite d'ergothérapeute",
          texte:
            "Le dispositif départemental prévoit un diagnostic du logement par un ergothérapeute avant les travaux. C'est un vrai plus : les préconisations sont établies à partir de vos gestes réels, pas d'un catalogue.",
        },
        {
          titre: "Un bâti mixte",
          texte:
            "Le nord du département (Asnières, Colombes, Gennevilliers) est très largement en collectif, le sud-ouest (Rueil, Marnes, Ville-d'Avray) beaucoup plus pavillonnaire. Les deux logiques de travaux — contrainte de copropriété d'un côté, question de l'étage de l'autre — coexistent ici.",
        },
      ],
    },
    aidesLocales: {
      preambule:
        "Le Département des Hauts-de-Seine a mis en place un dispositif d'aide à l'amélioration et à l'adaptation de l'habitat privé, dont l'instruction est confiée à un opérateur mandaté.",
      dispositifs: [
        {
          nom: "Aide départementale à l'adaptation de l'habitat privé",
          texte:
            "Accordée sous conditions de ressources, selon le barème de l'ANAH, l'aide départementale peut atteindre 4 200 € pour les ménages les plus modestes. Elle est majorable de 1 000 € pour les bénéficiaires de l'APA à domicile, les personnes dont le taux d'incapacité dépasse 50 % et l'ensemble des personnes de plus de 80 ans. Montants communiqués par le Département, à confirmer lors de l'instruction de votre dossier.",
          source:
            "https://www.hauts-de-seine.fr/mon-departement/les-hauts-de-seine/missions-et-actions/solidarites/habitat-prive",
        },
        {
          nom: "Diagnostic par un ergothérapeute",
          texte:
            "Le dispositif prend en charge un diagnostic du logement au regard de vos difficultés actuelles et de vos besoins à venir : conseils techniques et préconisations de travaux. SOLIHA Grand Paris, opérateur mandaté par le Département, accompagne les projets et instruit les demandes de financement.",
          source: "https://aidesetservices.hauts-de-seine.fr/",
        },
      ],
    },
        // Requêtes locales : « adaptation logement personne âgée <ville> » et
    // « aide salle de bain senior <département> ». Peu concurrentielles, et
    // le contenu reprend le contexte de bâti décrit plus haut — pas du
    // remplissage à mots-clés.
    local: {
      villes: "Boulogne-Billancourt, Nanterre, Asnières-sur-Seine, Colombes et Rueil-Malmaison",
      adaptation:
        "Dans les Hauts-de-Seine, l'adaptation du logement d'une personne âgée porte le plus souvent sur un appartement en copropriété. À Boulogne-Billancourt, Colombes ou Rueil-Malmaison, l'intervention se limite aux parties privatives et n'exige alors aucune assemblée générale — c'est ce qui permet de tenir des délais courts.",
      salleDeBain:
        "L'aide pour une salle de bain senior dans les Hauts-de-Seine peut être complétée par l'aide départementale à l'adaptation de l'habitat privé, et par un diagnostic d'ergothérapeute pris en charge par le Département.",
    },
communes: [
      "Boulogne-Billancourt",
      "Nanterre",
      "Asnières-sur-Seine",
      "Colombes",
      "Courbevoie",
      "Rueil-Malmaison",
      "Neuilly-sur-Seine",
      "Levallois-Perret",
      "Issy-les-Moulineaux",
      "Antony",
      "Clamart",
      "Meudon",
    ],
    faq: [
      {
        question: "L'aide du Département des Hauts-de-Seine se cumule-t-elle avec MaPrimeAdapt' ?",
        answer:
          "Oui, c'est précisément sa vocation : elle intervient en complément de l'aide de l'ANAH sur le même projet de travaux, sous conditions de ressources appréciées selon le barème de l'ANAH. Sur un dossier alto-séquanais, c'est la ligne qui fait la différence par rapport au même projet mené à Paris.",
      },
      {
        question: "Qui réalise le diagnostic d'ergothérapie et faut-il le payer ?",
        answer:
          "Le diagnostic est prévu par le dispositif départemental et réalisé avant les travaux. Il porte sur l'adéquation de votre logement à vos difficultés et à vos besoins futurs, et débouche sur des préconisations de travaux. SOLIHA Grand Paris, mandaté par le Département, en assure l'organisation dans le cadre de l'accompagnement.",
      },
      {
        question: "Vous êtes basés à Neuilly-sur-Seine : intervenez-vous dans tout le 92 ?",
        answer:
          "Oui, sur les 36 communes du département, de Gennevilliers à Antony. Notre implantation à Neuilly-sur-Seine nous permet des délais de visite courts dans les Hauts-de-Seine, généralement sous une semaine.",
      },
    ],
  },

  "seine-saint-denis": {
    code: "93",
    nom: "Seine-Saint-Denis",
    ou: "en Seine-Saint-Denis",
    de: "de Seine-Saint-Denis",
    intro:
      "La Seine-Saint-Denis a son propre programme d'aide aux propriétaires occupants, Rénov'Habitat 93, qui finance à la fois l'adaptation au vieillissement et la rénovation énergétique. Son intérêt : l'aide y est exprimée en pourcentage du montant des travaux, et elle vise explicitement les bénéficiaires de l'allocation départementale personnalisée d'autonomie.",
    contexte: {
      titre: "Ce qui change quand on habite la Seine-Saint-Denis",
      points: [
        {
          titre: "Rénov'Habitat 93",
          texte:
            "Le programme départemental couvre une part du coût des travaux d'adaptation, avec un taux plus élevé pour les ressources très modestes. Il a remplacé en 2014 l'ancienne aide du conseil général aux propriétaires occupants.",
        },
        {
          titre: "Beaucoup de copropriétés",
          texte:
            "Le département compte de nombreuses copropriétés des années 60 et 70, parfois fragiles. Quand les travaux touchent aux parties communes, le calendrier dépend de l'assemblée générale — c'est le point qui allonge le plus souvent les dossiers.",
        },
        {
          titre: "Un tissu pavillonnaire dense",
          texte:
            "À côté du collectif, des communes comme Le Raincy, Villemomble ou Gagny sont très pavillonnaires, avec la même problématique d'étage qu'en grande couronne.",
        },
      ],
    },
    aidesLocales: {
      preambule:
        "Le Département finance directement les travaux d'adaptation des propriétaires occupants, en pourcentage du montant des travaux.",
      dispositifs: [
        {
          nom: "Rénov'Habitat 93",
          texte:
            "Pour les travaux d'amélioration du logement liés au vieillissement, l'aide départementale représente 30 % du montant des travaux hors taxes pour les propriétaires aux ressources très modestes, et 20 % pour les ressources modestes. Le programme s'adresse aux bénéficiaires de l'ADPA de plus de 60 ans et aux propriétaires occupants sous les plafonds de ressources de l'ANAH. Ces taux ne sont plus publiés en ligne par le Département : faites-les confirmer par le bureau de l'habitat privé au 01 43 93 87 22 avant de les intégrer à votre plan de financement.",
          // Historique des sources, à garder pour ne pas refaire le tour :
          // - ressources.seine-saint-denis.fr n'a plus d'enregistrement DNS
          //   (constaté le 2026-08-12), la source d'origine pointait dans le vide.
          // - la page ANIL prise en remplacement ne mentionne plus du tout
          //   Rénov'Habitat 93 (vérifié le 2026-08-17 : zéro occurrence dans le
          //   HTML, la page n'est qu'un formulaire de recherche).
          // Faute de source officielle en ligne, on renvoie vers la page habitat
          // du Département et on donne le téléphone du bureau compétent, en
          // signalant dans le texte que les taux sont à confirmer.
          source: "https://seinesaintdenis.fr/ecologie-et-amenagement/habitat-logement/",
        },
        {
          nom: "Fonds de solidarité pour le logement (FSL)",
          texte:
            "Le FSL départemental n'intervient pas sur les travaux mais sur le maintien dans le logement en cas de difficulté à régler loyer ou charges. Il peut être utile en parallèle d'un projet d'adaptation quand la situation budgétaire est tendue. Service solidarité logement : 01 43 93 40 15.",
          source: "https://seinesaintdenis.fr/ecologie-et-amenagement/habitat-logement/",
        },
      ],
    },
        // Requêtes locales : « adaptation logement personne âgée <ville> » et
    // « aide salle de bain senior <département> ». Peu concurrentielles, et
    // le contenu reprend le contexte de bâti décrit plus haut — pas du
    // remplissage à mots-clés.
    local: {
      villes: "Saint-Denis, Montreuil, Aubervilliers, Aulnay-sous-Bois et Drancy",
      adaptation:
        "En Seine-Saint-Denis, l'adaptation du logement d'une personne âgée croise souvent une question d'état général du bâti. À Saint-Denis, Montreuil ou Aubervilliers, un dossier d'adaptation se double parfois d'un dossier de rénovation, et les deux se montent ensemble plutôt que l'un après l'autre.",
      salleDeBain:
        "L'aide pour une salle de bain senior en Seine-Saint-Denis peut s'articuler avec Rénov'Habitat 93 et, pour les ménages les plus modestes, avec le fonds de solidarité pour le logement. Nous vérifions les trois avant de déposer.",
    },
communes: [
      "Saint-Denis",
      "Montreuil",
      "Aubervilliers",
      "Aulnay-sous-Bois",
      "Drancy",
      "Noisy-le-Grand",
      "Pantin",
      "Le Blanc-Mesnil",
      "Bondy",
      "Épinay-sur-Seine",
      "Sevran",
      "Bobigny",
    ],
    faq: [
      {
        question: "Rénov'Habitat 93 s'ajoute-t-il à MaPrimeAdapt' ?",
        answer:
          "Oui. Rénov'Habitat 93 est une aide du Département aux propriétaires occupants, distincte de la subvention de l'ANAH. Pour les travaux liés au vieillissement, elle représente 30 % du montant hors taxes pour les ressources très modestes et 20 % pour les ressources modestes. Elle se demande auprès du Département, pas auprès de l'ANAH.",
      },
      {
        question: "Je suis bénéficiaire de l'ADPA, cela change-t-il quelque chose ?",
        answer:
          "Oui, les bénéficiaires de l'allocation départementale personnalisée d'autonomie de plus de 60 ans font partie du public visé par Rénov'Habitat 93. Par ailleurs, le volet aides techniques de l'ADPA peut financer certains équipements qui ne relèvent pas des travaux, comme un siège de douche ou une barre d'appui posée sans reprise du support.",
      },
      {
        question: "Mon immeuble est en copropriété : combien de temps cela ajoute-t-il ?",
        answer:
          "Si les travaux ne concernent que l'intérieur de votre logement — douche, WC, barres d'appui, éclairage — aucune formalité n'est nécessaire et le calendrier reste celui du dossier ANAH. Pour les parties communes, l'article 25-2 de la loi de 1965 dispense les travaux d'accessibilité réalisés aux frais du copropriétaire d'un vote d'autorisation : ils sont inscrits pour information à l'ordre du jour de la prochaine assemblée générale, qui ne peut s'y opposer que par une décision motivée. Le délai dépend donc surtout de la date de cette assemblée, en général annuelle.",
      },
    ],
  },

  "val-doise": {
    code: "95",
    nom: "Val-d'Oise",
    ou: "dans le Val-d'Oise",
    de: "du Val-d'Oise",
    intro:
      "Le Val-d'Oise a mis en place un guichet unique, Val d'Oise Rénov', porté par le Département avec les intercommunalités, l'ANAH et l'État. Il comporte un volet dédié à l'adaptation du logement à la perte d'autonomie, en plus du volet énergétique — ce qui simplifie beaucoup l'entrée dans le parcours pour les habitants du département.",
    contexte: {
      titre: "Ce qui change quand on habite le Val-d'Oise",
      points: [
        {
          titre: "Un guichet unique départemental",
          texte:
            "Val d'Oise Rénov' est un service public gratuit et neutre d'information, de conseil et d'orientation. Il traite l'adaptation à la perte d'autonomie comme un volet à part entière, avec des permanences sur le territoire et, dans certains cas, des visites à domicile.",
        },
        {
          titre: "Une majorité de maisons",
          texte:
            "Hors des grands ensembles de la vallée de Montmorency et de Sarcelles, le Val-d'Oise est très pavillonnaire. Maisons à étage, escaliers droits ou tournants, garages en demi-niveau : le monte-escalier y est l'une des demandes les plus fréquentes.",
        },
        {
          titre: "Un parc ancien à l'ouest",
          texte:
            "Dans le Vexin et autour de Pontoise, le bâti ancien en pierre pose des contraintes particulières : murs porteurs épais, sols irréguliers, salles de bain ajoutées après coup. L'élargissement de portes y demande une étude préalable.",
        },
      ],
    },
    aidesLocales: {
      preambule:
        "Le Département a regroupé l'information et l'orientation dans un service unique, joignable par téléphone.",
      dispositifs: [
        {
          nom: "Val d'Oise Rénov' Adaptation",
          texte:
            "Volet du service public départemental dédié au maintien à domicile : rendre le logement plus accessible et plus sûr. Il traite les travaux d'adaptation habituels — élargissement des portes, suppression de marches, rampe d'accès, monte-escalier, barres d'appui, douche de plain-pied, adaptation des commandes. Le détail de ce qui est subventionné n'est pas publié en ligne et s'apprécie au dossier : appelez un conseiller au 01 34 25 30 30, numéro général du Département.",
          source: "https://www.valdoise.fr/195-val-d-oise-renov.htm",
        },
        {
          nom: "Département — pôle personnes âgées",
          texte:
            "Pour les plus de 60 ans, le Département finance par ailleurs des services de maintien à domicile (aide ménagère, portage de repas, téléassistance) qui ne relèvent pas des travaux mais s'articulent avec eux.",
          source: "https://www.valdoise.fr/68-seniors.htm",
        },
      ],
    },
        // Requêtes locales : « adaptation logement personne âgée <ville> » et
    // « aide salle de bain senior <département> ». Peu concurrentielles, et
    // le contenu reprend le contexte de bâti décrit plus haut — pas du
    // remplissage à mots-clés.
    local: {
      villes: "Argenteuil, Cergy, Sarcelles, Garges-lès-Gonesse et Franconville",
      adaptation:
        "Dans le Val-d'Oise, l'adaptation du logement d'une personne âgée concerne aussi bien les pavillons d'Ermont ou de Franconville que les appartements d'Argenteuil et de Sarcelles. La configuration commande la solution : escalier extérieur et seuil de garage d'un côté, salle de bain exiguë de l'autre.",
      salleDeBain:
        "L'aide pour une salle de bain senior dans le Val-d'Oise se cumule avec Val d'Oise Rénov' Adaptation. Le pôle personnes âgées du Département peut également intervenir selon les ressources du foyer.",
    },
communes: [
      "Argenteuil",
      "Cergy",
      "Sarcelles",
      "Garges-lès-Gonesse",
      "Franconville",
      "Ermont",
      "Goussainville",
      "Pontoise",
      "Villiers-le-Bel",
      "Taverny",
      "Bezons",
      "Saint-Gratien",
    ],
    faq: [
      {
        question: "Dois-je passer par Val d'Oise Rénov' pour déposer mon dossier ?",
        answer:
          "Ce n'est pas obligatoire, mais c'est un point d'entrée gratuit et neutre, y compris pour vérifier les aides locales mobilisables sur votre commune. Le montage du dossier MaPrimeAdapt' proprement dit passe de toute façon par un assistant à maîtrise d'ouvrage : c'est ce que nous faisons, et l'accompagnement est financé par l'ANAH.",
      },
      {
        question: "Un monte-escalier est-il finançable dans une maison du Val-d'Oise ?",
        answer:
          "Oui, l'installation d'un monte-escalier fait partie des travaux d'accessibilité subventionnables. Sur un escalier privatif de maison individuelle, aucune autorisation extérieure n'est requise. Le prix dépend surtout de la forme de l'escalier : un rail droit coûte nettement moins cher qu'un rail courbe fabriqué sur mesure.",
      },
      {
        question: "Mon pavillon date des années 30 dans le Vexin, est-ce un obstacle ?",
        answer:
          "Non, mais cela demande une visite technique plus attentive. Murs porteurs épais, planchers irréguliers et réseaux anciens peuvent renchérir certains postes, notamment l'élargissement des portes et le déplacement d'évacuations. Ces surcoûts entrent dans l'assiette des travaux financés, à condition d'être identifiés dans les devis dès le départ.",
      },
    ],
  },
};

export const listeDepartements = () =>
  Object.entries(DEPARTEMENTS).map(([cle, d]) => ({ cle, ...d }));

export const trouverDepartement = (cle) => DEPARTEMENTS[cle] || null;
